import React, { useCallback, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";

import {
  TIPOS_HOJA,
  aTexto,
  construirImportacion,
  detectarTipoHoja,
  ubicarEncabezado,
  validarImportacion,
} from "../../domain/importacion.js";

/* ============================================================================
   Importar aeronave — pestaña de Gestión
   ----------------------------------------------------------------------------
   Toma las planillas que el operador ya tiene (status report, tabla de peso
   máximo, tabla de consumo, envolvente) y las convierte en un perfil de tipo y
   una entrada de flota.

   Toda la lógica de interpretación vive en domain/importacion.js, que es puro
   y está cubierto por tests. Acá solo se leen archivos y se pinta.

   MODO A PRUEBA DE FALLOS
   - Un archivo ilegible se reporta y no interrumpe a los demás.
   - Una hoja que falla no tumba las otras hojas del mismo archivo.
   - Una fila rota va a cuarentena y se muestra; el resto se carga.
   - Reimportar el mismo archivo REEMPLAZA sus hojas, no las duplica.
   - Nada se escribe en la flota ni en los perfiles hasta que el usuario
     confirma, y nunca si hay errores bloqueantes.
   ========================================================================== */

const LIMITES = {
  bytesPorArchivo: 12 * 1024 * 1024,
  filasPorHoja: 20000,
  archivosALaVez: 12,
  hojasPorArchivo: 40,
};

class LimiteDeError extends React.Component {
  constructor(p) { super(p); this.state = { fallo: null }; }
  static getDerivedStateFromError(e) { return { fallo: e }; }
  render() {
    if (this.state.fallo) {
      return (
        <div style={{ padding: 18, border: "1px solid #C6862B", borderRadius: 10, background: "#1A1204" }}>
          <div style={{ color: "#E8A33D", fontWeight: 700, marginBottom: 6 }}>
            La pantalla de importación falló, pero la aplicación sigue funcionando
          </div>
          <div style={{ fontSize: 13, color: "#C9CFCE", marginBottom: 8 }}>
            No se escribió nada en la flota ni en los perfiles. Cambia de pestaña y vuelve a entrar
            para reintentar; si se repite, guarda este texto:
          </div>
          <pre style={{ fontSize: 11, color: "#8A9997", overflow: "auto", margin: 0 }}>
            {String(this.state.fallo?.message || this.state.fallo)}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function ImportarAeronave({ COLORS, profiles, setProfiles, fleet, setFleet, onImportado }) {
  const [hojas, setHojas] = useState([]);
  const [registro, setRegistro] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [arrastrando, setArrastrando] = useState(false);
  const [aplicado, setAplicado] = useState(null);
  const inputRef = useRef(null);
  const hojasRef = useRef([]);

  const log = useCallback((nivel, mensaje) => {
    setRegistro((r) => [...r.slice(-99), { nivel, mensaje, t: new Date().toLocaleTimeString() }]);
  }, []);

  const leerArchivo = useCallback(async (file) => {
    const nuevas = [];
    try {
      if (file.size > LIMITES.bytesPorArchivo) {
        log("error", `"${file.name}" pesa ${(file.size / 1048576).toFixed(1)} MB y supera el límite de ${LIMITES.bytesPorArchivo / 1048576} MB.`);
        return nuevas;
      }
      const buf = await file.arrayBuffer();
      let libro;
      try {
        libro = XLSX.read(buf, { type: "array", cellDates: true });
      } catch (e) {
        log("error", `No se pudo abrir "${file.name}": ${e?.message || "formato no reconocido"}.`);
        return nuevas;
      }
      const nombres = (libro.SheetNames || []).slice(0, LIMITES.hojasPorArchivo);
      if (!nombres.length) {
        log("error", `"${file.name}" no tiene hojas legibles.`);
        return nuevas;
      }
      for (const nh of nombres) {
        try {
          const ws = libro.Sheets[nh];
          if (!ws) continue;
          let matriz = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: "" });
          if (!Array.isArray(matriz) || !matriz.length) continue;
          if (matriz.length > LIMITES.filasPorHoja) {
            log("aviso", `"${file.name} › ${nh}" tiene ${matriz.length} filas; se leen las primeras ${LIMITES.filasPorHoja}.`);
            matriz = matriz.slice(0, LIMITES.filasPorHoja);
          }
          // SheetJS no lanza excepción con un archivo dañado: devuelve una hoja
          // con basura. Hay que detectarlo contando celdas con contenido.
          let llenas = 0;
          for (const f of matriz) for (const c of f || []) if (aTexto(c) !== "") llenas++;
          if (llenas < 4 || matriz.length < 2) {
            log("error", `"${file.name} › ${nh}" no contiene una tabla legible (${llenas} celda(s) con datos). ¿El archivo está dañado o no es una planilla?`);
            continue;
          }
          const idx = ubicarEncabezado(matriz);
          const encabezados = (matriz[idx] || []).map(aTexto);
          const { tipo, confianza } = detectarTipoHoja(encabezados, nh);
          nuevas.push({
            id: `${file.name}::${nh}`,
            origen: `${file.name} › ${nh}`,
            encabezados,
            filas: matriz.slice(idx + 1),
            matriz,
            tipoDetectado: tipo,
            tipo,
            confianza,
            usar: Boolean(tipo),
          });
          log(tipo ? "ok" : "aviso",
            tipo
              ? `"${file.name} › ${nh}" → ${TIPOS_HOJA[tipo]} (${Math.round(confianza * 100)} % de confianza).`
              : `"${file.name} › ${nh}" no se pudo clasificar sola: asignale el tipo a mano.`);
        } catch (e) {
          log("error", `Falló la hoja "${nh}" de "${file.name}": ${e?.message}. Se sigue con las demás.`);
        }
      }
    } catch (e) {
      log("error", `Fallo inesperado con "${file?.name}": ${e?.message}`);
    }
    return nuevas;
  }, [log]);

  const recibirArchivos = useCallback(async (lista) => {
    const arr = Array.from(lista || []).slice(0, LIMITES.archivosALaVez);
    if (!arr.length) return;
    setCargando(true);
    setAplicado(null);
    try {
      for (const f of arr) {
        const nuevas = await leerArchivo(f);
        if (!nuevas.length) continue;
        const repetidas = nuevas.filter((n) => hojasRef.current.some((x) => x.id === n.id)).length;
        if (repetidas) log("aviso", `${repetidas} hoja(s) de "${f.name}" ya estaban cargadas: se reemplazaron por la versión nueva.`);
        setHojas((h) => {
          const mapa = new Map(h.map((x) => [x.id, x]));
          for (const n of nuevas) {
            const previo = mapa.get(n.id);
            mapa.set(n.id, previo ? { ...n, tipo: previo.tipo ?? n.tipo, usar: previo.usar } : n);
          }
          const res = Array.from(mapa.values());
          hojasRef.current = res;
          return res;
        });
      }
    } finally {
      setCargando(false);
    }
  }, [leerArchivo, log]);

  const importacion = useMemo(() => {
    try {
      return construirImportacion(hojas.filter((h) => h.usar && h.tipo));
    } catch (e) {
      return { perfil: null, aeronave: null, mantenimiento: [], cuarentena: [{ origen: "-", fila: "-", motivo: e?.message || "fallo", datos: "" }], anuladas: [] };
    }
  }, [hojas]);

  const val = useMemo(() => {
    try { return validarImportacion(importacion); } catch { return { errores: ["No se pudo validar la importación."], avisos: [] }; }
  }, [importacion]);

  const bloqueado = val.errores.length > 0 || !importacion.aeronave;

  const aplicar = useCallback(() => {
    if (bloqueado) return;
    const { perfil, aeronave } = importacion;
    try {
      setProfiles((prev) => {
        const lista = Array.isArray(prev) ? prev : [];
        const i = lista.findIndex((p) => p.id === perfil.id);
        if (i < 0) return [...lista, perfil];
        const copia = lista.slice();
        copia[i] = perfil;
        return copia;
      });
      setFleet((prev) => {
        const lista = Array.isArray(prev) ? prev : [];
        const i = lista.findIndex((f) => f.matricula === aeronave.matricula);
        if (i < 0) return [...lista, aeronave];
        // Se conserva lo que la aplicación ya sabía de esta matrícula y solo se
        // pisan los campos que la planilla realmente trae: reimportar un status
        // no puede borrar la enmienda de peso y balance cargada a mano.
        const copia = lista.slice();
        const conservado = copia[i];
        const soloConValor = Object.fromEntries(Object.entries(aeronave).filter(([, v]) => v !== null && v !== undefined));
        copia[i] = { ...conservado, ...soloConValor, id: conservado.id };
        return copia;
      });
      setAplicado({ matricula: aeronave.matricula, tipo: perfil.tipo, componentes: importacion.mantenimiento.length });
      log("ok", `Importada la matrícula ${aeronave.matricula} con el perfil "${perfil.tipo}".`);
      if (typeof onImportado === "function") onImportado(aeronave, perfil, importacion.mantenimiento);
    } catch (e) {
      log("error", `No se pudo guardar la importación: ${e?.message}`);
    }
  }, [bloqueado, importacion, setProfiles, setFleet, log, onImportado]);

  const C = COLORS || {};
  const panel = { background: C.panel || "#12201F", border: `1px solid ${C.hairline || "#243634"}`, borderRadius: 10, padding: 14, marginBottom: 14 };
  const titulo = { fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 15, letterSpacing: 0.6, textTransform: "uppercase", color: C.cyan || "#4FD1C5", marginBottom: 8 };
  const boton = (activo) => ({
    background: activo ? (C.cyan || "#4FD1C5") : "transparent",
    color: activo ? "#04201D" : (C.text || "#E6EDEC"),
    border: `1px solid ${activo ? (C.cyan || "#4FD1C5") : (C.hairline || "#243634")}`,
    borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: activo ? "pointer" : "not-allowed",
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.4,
  });
  const mono = { fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5 };

  const perfilesTotales = Array.isArray(profiles) ? profiles.length : 0;
  const flotaTotal = Array.isArray(fleet) ? fleet.length : 0;

  return (
    <LimiteDeError>
      <div>
        <div style={panel}>
          <div style={titulo}>Importar aeronave desde las planillas del operador</div>
          <p style={{ fontSize: 13, color: C.muted || "#8A9997", margin: "0 0 10px" }}>
            Suelta el status report, la tabla de peso máximo, la de consumo y la envolvente —juntos o por
            separado—. Cada hoja se clasifica sola; lo que no se reconozca se asigna a mano. Nada se guarda
            hasta que confirmes al final.
          </p>

          <div
            data-testid="zona-drop-importador"
            onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
            onDragLeave={() => setArrastrando(false)}
            onDrop={(e) => { e.preventDefault(); setArrastrando(false); recibirArchivos(e.dataTransfer?.files); }}
            onClick={() => inputRef.current?.click()}
            style={{
              border: `2px dashed ${arrastrando ? (C.amber || "#E8A33D") : (C.hairline || "#243634")}`,
              borderRadius: 10, padding: "26px 16px", textAlign: "center", cursor: "pointer",
              background: arrastrando ? "rgba(232,163,61,0.06)" : "transparent",
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 600, color: C.cyan || "#4FD1C5" }}>
              Arrastra los archivos acá, o haz clic para elegirlos
            </div>
            <div style={{ fontSize: 12, color: C.muted || "#8A9997", marginTop: 4 }}>
              Excel (.xlsx, .xls) o CSV · hasta {LIMITES.archivosALaVez} archivos · máx. {LIMITES.bytesPorArchivo / 1048576} MB cada uno
            </div>
            <input
              ref={inputRef}
              data-testid="input-archivos-importador"
              type="file"
              multiple
              accept=".xlsx,.xls,.csv"
              style={{ display: "none" }}
              onChange={(e) => {
                // Copia REAL: `e.target.files` es una lista viva y vaciar el
                // input la vacía también. Sin el Array.from no se carga nada.
                const archivos = Array.from(e.target.files || []);
                // Se limpia el valor del input DESPUÉS de tomar los archivos.
                // Sin esto, volver a elegir el mismo archivo no dispara
                // `change` y la app parece congelada justo cuando el usuario
                // corrige una planilla y la vuelve a cargar con el mismo nombre.
                e.target.value = "";
                recibirArchivos(archivos);
              }}
            />
          </div>
          {cargando && <div style={{ marginTop: 8, fontSize: 12, color: C.amber || "#E8A33D" }}>Leyendo archivos…</div>}
        </div>

        {registro.some((r) => r.nivel === "error") && (
          <div style={{ ...panel, borderColor: "#8C3B3B", background: "rgba(140,59,59,0.10)" }}>
            <div style={{ ...titulo, color: "#E58A8A" }}>Problemas durante la lectura</div>
            {registro.filter((r) => r.nivel === "error").slice(-5).map((r, i) => (
              <div key={i} style={{ fontSize: 12.5, color: "#D8BEBE" }}>• {r.mensaje}</div>
            ))}
          </div>
        )}

        {registro.some((r) => r.nivel === "aviso") && (
          <div style={{ ...panel, borderColor: C.amber || "#E8A33D" }}>
            <div style={{ ...titulo, color: C.amber || "#E8A33D" }}>Avisos de lectura</div>
            {registro.filter((r) => r.nivel === "aviso").slice(-5).map((r, i) => (
              <div key={i} style={{ fontSize: 12.5, color: C.text || "#E6EDEC" }}>• {r.mensaje}</div>
            ))}
          </div>
        )}

        {hojas.length > 0 && (
          <div style={panel}>
            <div style={titulo}>Hojas encontradas ({hojas.length})</div>
            {hojas.map((h, idx) => (
              <div key={h.id} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
                flexWrap: "wrap", padding: "7px 0", borderTop: idx ? `1px solid ${C.hairline || "#243634"}` : "none",
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ ...mono, color: C.text || "#E6EDEC" }}>{h.origen}</div>
                  <div style={{ fontSize: 11.5, color: C.muted || "#8A9997" }}>
                    {h.filas.length} filas · {h.encabezados.filter(Boolean).slice(0, 5).join(", ") || "sin encabezados"}
                    {h.tipoDetectado && h.confianza < 0.5 && (
                      <span style={{ color: C.amber || "#E8A33D" }}> · confianza baja, confirmalo</span>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <select
                    data-testid={`tipo-hoja-${idx}`}
                    value={h.tipo || ""}
                    onChange={(e) => {
                      const v = e.target.value || null;
                      setHojas((prev) => {
                        const res = prev.map((x, i) => (i === idx ? { ...x, tipo: v, usar: Boolean(v) } : x));
                        hojasRef.current = res;
                        return res;
                      });
                    }}
                    style={{
                      background: C.panelAlt || "#0C1817", color: C.text || "#E6EDEC",
                      border: `1px solid ${h.tipo ? (C.cyan || "#4FD1C5") : (C.amber || "#E8A33D")}`,
                      borderRadius: 6, padding: "5px 8px", fontSize: 12.5,
                    }}
                  >
                    <option value="">— sin clasificar —</option>
                    {Object.entries(TIPOS_HOJA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <button
                    onClick={() => setHojas((prev) => {
                      const res = prev.filter((_, i) => i !== idx);
                      hojasRef.current = res;
                      return res;
                    })}
                    style={{ background: "transparent", border: `1px solid ${C.hairline || "#243634"}`, color: C.muted || "#8A9997", borderRadius: 6, padding: "4px 9px", fontSize: 11.5, cursor: "pointer" }}
                  >quitar</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {hojas.some((h) => h.usar) && (
          <>
            <div style={{ ...panel, borderColor: bloqueado ? "#8C3B3B" : (C.cyan || "#4FD1C5") }}>
              <div style={{ ...titulo, color: bloqueado ? "#E58A8A" : (C.cyan || "#4FD1C5") }}>
                {bloqueado ? `${val.errores.length || 1} problema(s) que impiden importar` : "Lista para importar"}
              </div>
              {val.errores.map((e, i) => <div key={i} style={{ fontSize: 13, color: "#E58A8A", marginBottom: 3 }}>✕ {e}</div>)}
              {val.avisos.map((a, i) => <div key={i} style={{ fontSize: 13, color: C.amber || "#E8A33D", marginBottom: 3 }}>! {a}</div>)}
            </div>

            {importacion.anuladas.length > 0 && (
              <div style={panel}>
                <div style={{ ...titulo, color: C.amber || "#E8A33D" }}>
                  Celdas anuladas por imposibles ({importacion.anuladas.length})
                </div>
                <p style={{ fontSize: 12.5, color: C.muted || "#8A9997", margin: "0 0 8px" }}>
                  El peso máximo no puede subir cuando sube la temperatura ni cuando sube la altitud. Estas
                  celdas lo hacían, y en la dirección peligrosa: autorizaban más carga. Quedaron en hueco —
                  la aplicación dirá &quot;fuera de carta&quot; y mandará al manual. Relee estas celdas del RFM.
                </p>
                <div style={{ maxHeight: 170, overflow: "auto" }}>
                  {importacion.anuladas.slice(0, 60).map((a, i) => (
                    <div key={i} style={{ ...mono, color: C.text || "#E6EDEC", fontSize: 11.5, padding: "2px 0" }}>
                      {a.carta} · {a.alt} ft / {a.temp} °C = {a.valor} kg — {a.motivo}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {importacion.cuarentena.length > 0 && (
              <div style={panel}>
                <div style={{ ...titulo, color: C.amber || "#E8A33D" }}>
                  Cuarentena ({importacion.cuarentena.length} fila(s) ilegibles — el resto sí se cargó)
                </div>
                <div style={{ maxHeight: 150, overflow: "auto" }}>
                  {importacion.cuarentena.slice(0, 60).map((q, i) => (
                    <div key={i} style={{ fontSize: 11.5, color: C.muted || "#8A9997", padding: "2px 0" }}>
                      {q.origen} · fila {q.fila} — {q.motivo}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={panel}>
              <div style={titulo}>Vista previa</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div>
                  <div style={{ fontSize: 12, color: C.muted || "#8A9997", marginBottom: 4 }}>AERONAVE</div>
                  {importacion.aeronave ? (
                    <table style={{ width: "100%", ...mono }}>
                      <tbody>
                        {[
                          ["Matrícula", importacion.aeronave.matricula],
                          ["Tipo", importacion.aeronave.tipo],
                          ["Serie", importacion.aeronave.serie || "—"],
                          ["Peso básico vacío", importacion.aeronave.pesoBasicoVacioKg != null ? `${importacion.aeronave.pesoBasicoVacioKg} kg` : null],
                          ["Brazo vacío", importacion.aeronave.brazoVacioM != null ? `${importacion.aeronave.brazoVacioM} m` : null],
                          ["Fecha de pesaje", importacion.aeronave.fechaPesaje],
                          ["Peso máx. despegue", importacion.aeronave.pesoMaxDespegueKg != null ? `${importacion.aeronave.pesoMaxDespegueKg} kg` : null],
                          ["TSN", importacion.aeronave.tsn],
                          ["CSN", importacion.aeronave.csn],
                        ].map(([k, v]) => (
                          <tr key={k}>
                            <td style={{ color: C.muted || "#8A9997", padding: "1px 0" }}>{k}</td>
                            <td style={{ textAlign: "right", color: v ? (C.text || "#E6EDEC") : (C.amber || "#E8A33D") }}>
                              {v || "sin dato"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <div style={{ fontSize: 12.5, color: C.amber || "#E8A33D" }}>Sin matrícula no se crea la aeronave.</div>}
                </div>
                <div>
                  <div style={{ fontSize: 12, color: C.muted || "#8A9997", marginBottom: 4 }}>PERFIL DE TIPO</div>
                  <table style={{ width: "100%", ...mono }}>
                    <tbody>
                      {[
                        ["Brazos mapeados", Object.keys(importacion.perfil?.arms || {}).length || null],
                        ["Compartimentos", importacion.perfil?.cargoCompartments?.length || null],
                        ["Envolvente", importacion.perfil?.envelope ? `${importacion.perfil.envelope.forward.length} puntos` : null],
                        ["Carta OGE", importacion.perfil?.ogeTable ? `${importacion.perfil.ogeTable.alts.length} × ${importacion.perfil.ogeTable.temps.length}` : null],
                        ["Carta IGE", importacion.perfil?.igeTable ? `${importacion.perfil.igeTable.alts.length} × ${importacion.perfil.igeTable.temps.length}` : null],
                        ["Carta de consumo", importacion.perfil?.fuelTable ? `${importacion.perfil.fuelTable.alts.length} × ${importacion.perfil.fuelTable.temps.length}` : null],
                        ["Componentes", importacion.mantenimiento.length || null],
                      ].map(([k, v]) => (
                        <tr key={k}>
                          <td style={{ color: C.muted || "#8A9997", padding: "1px 0" }}>{k}</td>
                          <td style={{ textAlign: "right", color: v ? (C.text || "#E6EDEC") : (C.amber || "#E8A33D") }}>{v || "sin dato"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {importacion.perfil?.arms && Object.keys(importacion.perfil.arms).length > 0 && (
                <div style={{ marginTop: 10, fontSize: 11.5, color: C.muted || "#8A9997", ...mono }}>
                  Brazos: {Object.entries(importacion.perfil.arms).map(([k, v]) => `${k}=${v}`).join("  ")}
                </div>
              )}

              <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <button data-testid="btn-importar" onClick={aplicar} disabled={bloqueado} style={boton(!bloqueado)}>
                  Importar a la flota
                </button>
                <span style={{ fontSize: 12, color: C.muted || "#8A9997" }}>
                  Hoy: {flotaTotal} aeronave(s), {perfilesTotales} perfil(es).
                </span>
              </div>

              {aplicado && (
                <div data-testid="importacion-ok" style={{ marginTop: 12, padding: 10, borderRadius: 8, border: `1px solid ${C.cyan || "#4FD1C5"}`, background: "rgba(79,209,197,0.07)" }}>
                  <div style={{ color: C.cyan || "#4FD1C5", fontWeight: 600, fontSize: 13.5 }}>
                    {aplicado.matricula} importada con el perfil &quot;{aplicado.tipo}&quot;.
                  </div>
                  <div style={{ fontSize: 12.5, color: C.amber || "#E8A33D", marginTop: 4 }}>
                    Queda marcada como NO VERIFICADA. Antes de despachar con ella hay que cotejar la
                    envolvente y las cartas contra el RFM, y el peso vacío contra el acta de pesaje.
                    {aplicado.componentes > 0 && ` Se leyeron ${aplicado.componentes} componente(s) de mantenimiento.`}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </LimiteDeError>
  );
}
